<script setup>
import { useI18n } from '../../i18n.js'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/vue/24/solid'
import Pts2EntityBadge from './Pts2EntityBadge.vue'

const { locale, t } = useI18n()

const props = defineProps({ detail: { type: Object, default: null }, loading: Boolean, error: { type: String, default: '' } })
const emit = defineEmits(['close'])
const panel = ref(null)
let previousFocus

const alarmLabels = computed(() => ({
  CriticalHighProduct: t('pts2UiHistoryCriticalHigh'), HighProduct: t('pts2UiHistoryHighProduct'),
  LowProduct: t('pts2UiHistoryLowProduct'), CriticalLowProduct: t('pts2UiHistoryCriticalLow'),
  HighWater: t('pts2UiHistoryHighWater'), TankLeakage: t('pts2UiHistoryLeakage'),
}))

function close() { emit('close') }
function keydown(event) {
  if (event.key === 'Escape') { event.preventDefault(); close(); return }
  if (event.key !== 'Tab') return
  const items = [...(panel.value?.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"])') ?? [])]
    .filter((item) => !item.disabled)
  if (!items.length) return
  const first = items[0], last = items.at(-1)
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
}
function number(value, digits = 1) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : String(value)
}
function integer(value) {
  if (value === null || value === undefined || value === '') return '—'
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed.toLocaleString(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', { maximumFractionDigits: 0 }) : String(value)
}
function date(value, timeZone) {
  if (!value) return '—'
  const parsed = new Date(value)
  if (!Number.isFinite(parsed.getTime())) return String(value)
  const format = { dateStyle: 'short', timeStyle: 'medium' }
  if (timeZone) format.timeZone = timeZone
  return new Intl.DateTimeFormat(({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA', format).format(parsed)
}
function state(value) {
  if (value === 'ERROR') return { label: t('pts2UiHistoryError'), icon: XCircleIcon }
  if (value === 'ALARM') return { label: t('pts2UiHistoryAlarm'), icon: ExclamationTriangleIcon }
  if (value === 'INCOMPLETE') return { label: t('pts2UiHistoryIncomplete'), icon: InformationCircleIcon }
  return { label: t('pts2UiHistoryNormal'), icon: CheckCircleIcon }
}
function alarms(measurement) {
  if (measurement.alarmDataState !== 'KNOWN') return t('pts2UiHistoryIncompleteAlarms')
  return measurement.alarms.length ? measurement.alarms.map((item) => alarmLabels.value[item] || item).join(', ') : t('pts2UiHistoryNone')
}

onMounted(async () => {
  previousFocus = document.activeElement
  document.body.classList.add('drawer-open')
  await nextTick(); panel.value?.querySelector('.pts2-drawer-close')?.focus()
})
onBeforeUnmount(() => { document.body.classList.remove('drawer-open'); previousFocus?.focus?.() })
</script>

<template>
  <div class="pts2-transaction-drawer-layer" @keydown="keydown">
    <button class="pts2-transaction-backdrop" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"></button>
    <aside ref="panel" class="pts2-transaction-drawer pts2-measurement-drawer" role="dialog" aria-modal="true"
      aria-labelledby="pts2-measurement-title">
      <header><div><small>{{ t('pts2UiHistoryMeasurementDetails') }}</small><h2 id="pts2-measurement-title">{{ t('pts2UiHistoryTankNumber', { number: detail?.measurement?.tankNumber ?? '—' }) }}</h2></div>
        <button class="pts2-drawer-close" type="button" :aria-label="t('pts2UiHistoryCloseDetails')" @click="close"><XMarkIcon aria-hidden="true" /></button>
      </header>
      <p v-if="loading" class="pts2-drawer-state" role="status">{{ t('pts2UiHistoryLoadingDetails') }}</p>
      <p v-else-if="error" class="notice error" role="alert">{{ error }}</p>
      <div v-else-if="detail" class="pts2-transaction-detail pts2-measurement-detail">
        <section class="pts2-transaction-summary pts2-measurement-summary">
          <span>{{ t('pts2UiHistoryFilling') }}</span><strong>{{ number(detail.fillingPercentage, 0) }}{{ detail.fillingPercentage == null ? '' : ' %' }}</strong>
          <div><p><span>{{ t('pts2UiHistoryProductVolume') }}</span><b>{{ integer(detail.measurement.productVolume) }} {{ t('pts2UiHistoryLiters') }}</b></p>
            <p><span>{{ t('pts2UiHistoryUllage') }}</span><b>{{ integer(detail.productUllage) }} {{ t('pts2UiHistoryLiters') }}</b></p>
            <p><span>{{ t('pts2UiHistoryFuel') }}</span><Pts2EntityBadge :entity-id="detail.measurement.fuelGradeId" :label="detail.measurement.fuelGradeName || '—'" /></p></div>
        </section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryLevels') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryProductHeight') }}<small>ProductHeight</small></dt><dd>{{ number(detail.measurement.productHeight) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryCompensatedLiters') }}<small>{{ t('pts2UiHistoryCompensatedProductTC') }}</small></dt><dd>{{ integer(detail.productTcVolume) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryWaterHeight') }}</dt><dd>{{ number(detail.measurement.waterHeight) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryWaterVolume') }}</dt><dd>{{ integer(detail.waterVolume) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryUllageLiters') }}</dt><dd>{{ integer(detail.productUllage) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryConditions') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryTemperature') }}</dt><dd>{{ number(detail.measurement.temperature) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryProductDensity') }}</dt><dd>{{ number(detail.productDensity) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryProductMass') }}</dt><dd>{{ number(detail.productMass) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryMeasurementState') }}</dt><dd class="pts2-measurement-state" :class="detail.measurement.displayState.toLowerCase()"><component :is="state(detail.measurement.displayState).icon" aria-hidden="true" />{{ state(detail.measurement.displayState).label }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryAlarms') }}</dt><dd>{{ alarms(detail.measurement) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryMeasurementTime') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryMeasured') }}</dt><dd>{{ date(detail.measurement.eventAt, detail.measurement.stationTimeZone) }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryReceived') }}</dt><dd>{{ date(detail.measurement.receivedAt, detail.measurement.stationTimeZone) }}</dd></div>
        </dl></section>
        <section class="pts2-transaction-section"><h3>{{ t('pts2UiHistoryProvenance') }}</h3><dl>
          <div class="wide"><dt>{{ t('pts2UiHistoryStation') }}</dt><dd>{{ detail.stationName || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryController') }}</dt><dd>{{ detail.controllerName || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryPacket') }}</dt><dd>{{ detail.packetType }}</dd></div>
          <div class="wide"><dt>ConfigurationId</dt><dd>{{ detail.configurationId || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryTransport') }}</dt><dd>{{ detail.transport || '—' }}</dd></div>
          <div class="wide"><dt>{{ t('pts2UiHistoryUploadedMeasurements') }}</dt><dd>{{ t('pts2UiHistoryOf', { count: detail.measurementsUploaded ?? '—', total: detail.measurementsTotal ?? '—' }) }}</dd></div>
        </dl></section>
      </div>
      <footer><button class="pc-secondary" type="button" @click="close">{{ t('pts2UiHistoryClose') }}</button></footer>
    </aside>
  </div>
</template>
